import { ReactElement, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { MenuItemLink, MenuProps, Sidebar, useSidebarState } from 'react-admin'
import MenuFolderComponent from './MenuFolder'
import { menuItems } from './menuItems'
import { isMultirole, matchesRolesTypes, RoleType } from '@/authProvider'

type MenuState = {
  [key: string]: boolean
}

export type MenuLink = {
  to: string
  name: string
  icon: ReactElement
  multirole?: boolean
  role?: RoleType
  roles?: RoleType[]
}

export type MenuFolder = {
  name: string
  icon: ReactElement
  unfolded?: boolean
  items: MenuLink[]
  multirole?: boolean
  role?: RoleType
  roles?: RoleType[]
}

const Menu = ({ dense = false }: MenuProps) => {
  const [state, setState] = useState<MenuState>({})
  const [open] = useSidebarState()

  const handleToggle = (menu: string) => {
    setState((state) => ({ ...state, [menu]: !state[menu] }))
  }

  const renderMenuLink = (item: MenuLink) => (
    <MenuItemLink
      key={item.to}
      to={item.to}
      primaryText={item.name}
      leftIcon={item.icon}
      dense={dense}
    />
  )

  const renderMenuFolder = (item: MenuFolder) => {
    const validItems = item.items.filter((subItem) => {
      if (subItem.multirole && !isMultirole()) return false

      if (
        (subItem.role && !matchesRolesTypes([subItem.role])) ||
        (subItem.roles && !matchesRolesTypes(subItem.roles))
      )
        return false

      return true
    })

    if (validItems.length === 0) return null

    return (
      <MenuFolderComponent
        key={item.name}
        handleToggle={() => handleToggle(item.name)}
        isOpen={state[item.name]}
        dense={dense}
        name={item.name}
        icon={item.icon}
      >
        {validItems.map(renderMenuLink)}
      </MenuFolderComponent>
    )
  }

  const renderMenuItem = (item: MenuLink | MenuFolder) => {
    if (item.multirole && !isMultirole()) return

    if (
      (item.role && !matchesRolesTypes([item.role])) ||
      (item.roles && !matchesRolesTypes(item.roles))
    )
      return

    if ('to' in item) {
      return renderMenuLink(item)
    }

    return renderMenuFolder(item)
  }

  useEffect(() => {
    const initialState: MenuState = {}
    menuItems.forEach((item) => {
      if ('unfolded' in item) {
        initialState[item.name] = item.unfolded || false
      }
    })
    setState(initialState)
  }, [])

  return (
    <Sidebar
      sx={{
        width: open ? 200 : 50,
        marginTop: 1,
        marginBottom: 1,
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
          })
      }}
    >
      <Box>{menuItems.map(renderMenuItem)}</Box>
    </Sidebar>
  )
}

export default Menu
